import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { UserPlus, Plus, IndianRupee, Edit, Trash2, RotateCcw, Banknote, Eye, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Workers() {
  const [workers, setWorkers] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [advances, setAdvances] = useState([]);
  const [showWorkerDialog, setShowWorkerDialog] = useState(false);
  const [showAdvanceDialog, setShowAdvanceDialog] = useState(false);
  const [showEditWorkerDialog, setShowEditWorkerDialog] = useState(false);
  const [showDeleteWorkerDialog, setShowDeleteWorkerDialog] = useState(false);
  const [showResetAdvancesDialog, setShowResetAdvancesDialog] = useState(false);
  const [showPaySalaryDialog, setShowPaySalaryDialog] = useState(false);
  const [showSalaryDetailsDialog, setShowSalaryDetailsDialog] = useState(false);
  const [salaryDetails, setSalaryDetails] = useState(null);
  const [loadingSalaryDetails, setLoadingSalaryDetails] = useState(false);
  const [payingSalary, setPayingSalary] = useState(null);
  const [payAmount, setPayAmount] = useState(0);
  const [payDate, setPayDate] = useState(new Date().toISOString().split('T')[0]);
  const [payNotes, setPayNotes] = useState('');
  const [editingWorker, setEditingWorker] = useState(null);
  const [deletingWorker, setDeletingWorker] = useState(null);
  const [dateRange, setDateRange] = useState({
    start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0]
  });
  const [workerForm, setWorkerForm] = useState({ name: '', phone: '' });
  const [advanceForm, setAdvanceForm] = useState({
    worker_id: '',
    amount: 0,
    date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, [dateRange]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [workersRes, salariesRes] = await Promise.all([
        axios.get(`${API}/workers`, { headers }),
        axios.get(`${API}/salary?start_date=${dateRange.start_date}&end_date=${dateRange.end_date}`, { headers })
      ]);
      setWorkers(workersRes.data);
      setSalaries(salariesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const fetchAdvances = async (workerId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/advances/${workerId}`, { headers });
      setAdvances(res.data);
    } catch (error) {
      console.error('Error fetching advances:', error);
    }
  };

  const fetchSalaryDetails = async (workerId) => {
    setLoadingSalaryDetails(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(
        `${API}/salary/details/${workerId}?start_date=${dateRange.start_date}&end_date=${dateRange.end_date}`,
        { headers }
      );
      setSalaryDetails(res.data);
      setShowSalaryDetailsDialog(true);
    } catch (error) {
      console.error('Error fetching salary details:', error);
      toast.error('Failed to load salary details');
    } finally {
      setLoadingSalaryDetails(false);
    }
  };

  const handleAddWorker = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/workers`, workerForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Worker added successfully');
      setWorkerForm({ name: '', phone: '' });
      setShowWorkerDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to add worker');
    }
  };

  const handleAddAdvance = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/advances`, advanceForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Advance payment recorded');
      setAdvanceForm({
        worker_id: '',
        amount: 0,
        date: new Date().toISOString().split('T')[0],
        notes: ''
      });
      setShowAdvanceDialog(false);
      fetchData();
      if (selectedWorker) {
        fetchAdvances(selectedWorker);
      }
    } catch (error) {
      toast.error('Failed to record advance');
    }
  };

  const handleEditWorker = (worker) => {
    setEditingWorker({ id: worker.id, name: worker.name, phone: worker.phone });
    setShowEditWorkerDialog(true);
  };

  const handleUpdateWorker = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/workers/${editingWorker.id}`, {
        name: editingWorker.name,
        phone: editingWorker.phone
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Worker updated successfully');
      setShowEditWorkerDialog(false);
      setEditingWorker(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update worker');
    }
  };

  const handleDeleteWorkerConfirm = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/workers/${deletingWorker.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Worker deleted successfully');
      setShowDeleteWorkerDialog(false);
      setDeletingWorker(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete worker');
    }
  };

  const handleResetAdvances = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/workers/${selectedWorker}/reset-advances`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Worker advances reset successfully');
      setShowResetAdvancesDialog(false);
      setSelectedWorker(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to reset advances');
    }
  };

  const handlePaySalary = async () => {
    if (payAmount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/workers/${payingSalary.worker_id}/pay-salary`, 
        { worker_id: payingSalary.worker_id, amount: payAmount, date: payDate, notes: payNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Salary payment of ₹${payAmount} recorded successfully`);
      setShowPaySalaryDialog(false);
      setPayingSalary(null);
      setPayAmount(0);
      setPayDate(new Date().toISOString().split('T')[0]);
      setPayNotes('');
      fetchData();
    } catch (error) {
      toast.error('Failed to record salary payment');
    }
  };

  const handleResetSalaryPaid = async (workerId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/workers/${workerId}/reset-salary-paid`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Salary paid reset successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to reset salary paid');
    }
  };

  return (
    <div data-testid="workers-page" className="space-y-8">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="font-manrope font-bold text-4xl text-concrete-900 tracking-tight" data-testid="workers-title">Workers & Salary</h1>
          <p className="font-inter text-concrete-600 mt-2">Manage workers and calculate salaries</p>
        </div>
        <div className="flex gap-3">
          <Dialog open={showAdvanceDialog} onOpenChange={setShowAdvanceDialog}>
            <DialogTrigger asChild>
              <Button data-testid="add-advance-button" className="bg-brick-600 hover:bg-brick-500 text-white shadow-md font-manrope font-semibold">
                <IndianRupee className="mr-2 h-4 w-4" />
                Add Advance
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-manrope font-bold">Record Advance Payment</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddAdvance} className="space-y-4">
                <div>
                  <Label className="font-manrope font-semibold uppercase tracking-wider text-xs">Worker</Label>
                  <select
                    data-testid="advance-worker-select"
                    value={advanceForm.worker_id}
                    onChange={(e) => setAdvanceForm({ ...advanceForm, worker_id: e.target.value })}
                    className="w-full mt-2 px-3 py-2 border border-concrete-300 rounded-md focus:border-brick-500 focus:ring-brick-500/20 focus:ring-2"
                    required
                  >
                    <option value="">Select worker</option>
                    {workers.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="font-manrope font-semibold uppercase tracking-wider text-xs">Amount (₹)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    data-testid="advance-amount"
                    value={advanceForm.amount}
                    onChange={(e) => setAdvanceForm({ ...advanceForm, amount: parseFloat(e.target.value) || 0 })}
                    className="mt-2 tabular-nums"
                    required
                  />
                </div>
                <div>
                  <Label className="font-manrope font-semibold uppercase tracking-wider text-xs">Date</Label>
                  <Input
                    type="date"
                    data-testid="advance-date"
                    value={advanceForm.date}
                    onChange={(e) => setAdvanceForm({ ...advanceForm, date: e.target.value })}
                    className="mt-2"
                    required
                  />
                </div>
                <div>
                  <Label className="font-manrope font-semibold uppercase tracking-wider text-xs">Notes (Optional)</Label>
                  <Textarea
                    data-testid="advance-notes"
                    value={advanceForm.notes}
                    onChange={(e) => setAdvanceForm({ ...advanceForm, notes: e.target.value })}
                    className="mt-2"
                    rows={2}
                  />
                </div>
                <Button type="submit" className="w-full bg-brick-600 hover:bg-brick-500">
                  Record Advance
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={showWorkerDialog} onOpenChange={setShowWorkerDialog}>
            <DialogTrigger asChild>
              <Button data-testid="add-worker-button" className="bg-brick-600 hover:bg-brick-500 text-white shadow-md font-manrope font-semibold">
                <UserPlus className="mr-2 h-4 w-4" />
                Add Worker
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-manrope font-bold">Add New Worker</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddWorker} className="space-y-4">
                <div>
                  <Label className="font-manrope font-semibold uppercase tracking-wider text-xs">Worker Name</Label>
                  <Input
                    data-testid="worker-name-input"
                    value={workerForm.name}
                    onChange={(e) => setWorkerForm({ ...workerForm, name: e.target.value })}
                    className="mt-2"
                    required
                  />
                </div>
                <div>
                  <Label className="font-manrope font-semibold uppercase tracking-wider text-xs">Phone Number</Label>
                  <Input
                    data-testid="worker-phone-input"
                    value={workerForm.phone}
                    onChange={(e) => setWorkerForm({ ...workerForm, phone: e.target.value })}
                    className="mt-2"
                    required
                  />
                </div>
                <Button type="submit" className="w-full bg-brick-600 hover:bg-brick-500">
                  Add Worker
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs defaultValue="salary" className="w-full">
        <TabsList className="bg-white border border-concrete-200">
          <TabsTrigger data-testid="salary-tab" value="salary" className="font-manrope font-semibold">Salary Calculation</TabsTrigger>
          <TabsTrigger data-testid="workers-tab" value="workers" className="font-manrope font-semibold">All Workers</TabsTrigger>
        </TabsList>

        <TabsContent value="salary" className="space-y-6 mt-6">
          <div className="bg-white border border-concrete-200 shadow-sm rounded-md p-6">
            <h3 className="font-manrope font-bold text-lg mb-4">Select Date Range</h3>
            
            {/* Quick Date Range Presets */}
            <div className="flex flex-wrap gap-2 mb-4">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  const today = new Date();
                  const dayOfWeek = today.getDay();
                  const monday = new Date(today);
                  monday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
                  setDateRange({
                    start_date: monday.toISOString().split('T')[0],
                    end_date: today.toISOString().split('T')[0]
                  });
                }}
                className="text-xs"
              >
                This Week
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  const today = new Date();
                  const dayOfWeek = today.getDay();
                  const lastMonday = new Date(today);
                  lastMonday.setDate(today.getDate() - (dayOfWeek === 0 ? 13 : dayOfWeek + 6));
                  const lastSunday = new Date(lastMonday);
                  lastSunday.setDate(lastMonday.getDate() + 6);
                  setDateRange({
                    start_date: lastMonday.toISOString().split('T')[0],
                    end_date: lastSunday.toISOString().split('T')[0]
                  });
                }}
                className="text-xs"
              >
                Last Week
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  const today = new Date();
                  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
                  setDateRange({
                    start_date: firstDay.toISOString().split('T')[0],
                    end_date: today.toISOString().split('T')[0]
                  });
                }}
                className="text-xs"
              >
                This Month
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  const today = new Date();
                  const firstDay = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                  const lastDay = new Date(today.getFullYear(), today.getMonth(), 0);
                  setDateRange({
                    start_date: firstDay.toISOString().split('T')[0],
                    end_date: lastDay.toISOString().split('T')[0]
                  });
                }}
                className="text-xs"
              >
                Last Month
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="font-manrope font-semibold uppercase tracking-wider text-xs">Start Date</Label>
                <Input
                  type="date"
                  data-testid="salary-start-date"
                  value={dateRange.start_date}
                  onChange={(e) => setDateRange({ ...dateRange, start_date: e.target.value })}
                  className="mt-2"
                />
              </div>
              <div>
                <Label className="font-manrope font-semibold uppercase tracking-wider text-xs">End Date</Label>
                <Input
                  type="date"
                  data-testid="salary-end-date"
                  value={dateRange.end_date}
                  onChange={(e) => setDateRange({ ...dateRange, end_date: e.target.value })}
                  className="mt-2"
                />
              </div>
            </div>
            
            <div className="mt-3 text-sm text-concrete-600">
              Showing salary for: <span className="font-semibold">{dateRange.start_date}</span> to <span className="font-semibold">{dateRange.end_date}</span>
            </div>
          </div>

          <div data-testid="salary-table" className="bg-white border border-concrete-200 shadow-sm rounded-md overflow-hidden">
            <div className="p-6 border-b border-concrete-200">
              <h2 className="font-manrope font-bold text-xl text-concrete-900">Salary Details</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-concrete-50 border-b border-concrete-200 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Worker</th>
                    <th className="px-4 py-3 text-right font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Fly Ash</th>
                    <th className="px-4 py-3 text-right font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">6" Interlock</th>
                    <th className="px-4 py-3 text-right font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">8" Interlock</th>
                    <th className="px-4 py-3 text-right font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Production</th>
                    <th className="px-4 py-3 text-right font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Loading</th>
                    <th className="px-4 py-3 text-right font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Advances</th>
                    <th className="px-4 py-3 text-right font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Paid</th>
                    <th className="px-4 py-3 text-right font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Balance</th>
                    <th className="px-4 py-3 text-center font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-concrete-100">
                  {salaries.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="px-6 py-12 text-center font-inter text-concrete-500">No salary data for selected period</td>
                    </tr>
                  ) : (
                    salaries.map((salary) => (
                      <tr key={salary.worker_id} className="hover:bg-concrete-50">
                        <td className="px-4 py-4 font-inter text-concrete-700 font-medium">{salary.worker_name}</td>
                        <td className="px-4 py-4 font-inter text-concrete-900 text-right tabular-nums">{salary.fly_ash_count}</td>
                        <td className="px-4 py-4 font-inter text-concrete-900 text-right tabular-nums">{salary.interlock_6_count}</td>
                        <td className="px-4 py-4 font-inter text-concrete-900 text-right tabular-nums">{salary.interlock_8_count}</td>
                        <td className="px-4 py-4 font-inter font-semibold text-concrete-900 text-right tabular-nums">₹{salary.total_salary.toFixed(2)}</td>
                        <td className="px-4 py-4 font-inter font-semibold text-green-600 text-right tabular-nums">+₹{salary.loading_earnings?.toFixed(2) || '0.00'}</td>
                        <td className="px-4 py-4 font-inter text-red-600 text-right tabular-nums">-₹{salary.total_advances.toFixed(2)}</td>
                        <td className="px-4 py-4 font-inter text-blue-600 text-right tabular-nums">-₹{salary.salary_paid?.toFixed(2) || '0.00'}</td>
                        <td className="px-4 py-4 font-inter font-bold text-brick-600 text-right tabular-nums">₹{salary.final_salary.toFixed(2)}</td>
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              onClick={() => fetchSalaryDetails(salary.worker_id)}
                              size="sm"
                              variant="outline"
                              className="text-blue-600 border-blue-300 hover:bg-blue-50 text-xs px-2 py-1 h-7"
                              disabled={loadingSalaryDetails}
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              Details
                            </Button>
                            <Button
                              onClick={() => {
                                setPayingSalary(salary);
                                setPayAmount(salary.final_salary > 0 ? salary.final_salary : 0);
                                setShowPaySalaryDialog(true);
                              }}
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white text-xs px-2 py-1 h-7"
                              disabled={salary.final_salary <= 0}
                            >
                              <Banknote className="h-3 w-3 mr-1" />
                              Pay
                            </Button>
                            {(salary.salary_paid || 0) > 0 && (
                              <Button
                                onClick={() => handleResetSalaryPaid(salary.worker_id)}
                                size="sm"
                                variant="ghost"
                                className="text-orange-600 hover:text-orange-700 hover:bg-orange-50 h-7 px-2"
                                title="Reset Paid Amount"
                              >
                                <RotateCcw className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="workers" className="mt-6">
          <div data-testid="workers-list" className="bg-white border border-concrete-200 shadow-sm rounded-md overflow-hidden">
            <div className="p-6 border-b border-concrete-200">
              <h2 className="font-manrope font-bold text-xl text-concrete-900">All Workers</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-concrete-50 border-b border-concrete-200">
                  <tr>
                    <th className="px-6 py-3 text-left font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Name</th>
                    <th className="px-6 py-3 text-left font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Phone</th>
                    <th className="px-6 py-3 text-right font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Total Advances</th>
                    <th className="px-6 py-3 text-center font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-concrete-100">
                  {workers.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="px-6 py-12 text-center font-inter text-concrete-500">No workers added yet</td>
                    </tr>
                  ) : (
                    workers.map((worker) => (
                      <tr key={worker.id} className="hover:bg-concrete-50">
                        <td className="px-6 py-4 font-inter text-concrete-700 font-medium">{worker.name}</td>
                        <td className="px-6 py-4 font-inter text-concrete-700">{worker.phone}</td>
                        <td className="px-6 py-4 font-inter font-semibold text-brick-600 text-right tabular-nums">₹{worker.total_advances?.toFixed(2) || '0.00'}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-center gap-2">
                            <Button
                              data-testid={`edit-worker-${worker.id}`}
                              onClick={() => handleEditWorker(worker)}
                              size="sm"
                              variant="ghost"
                              className="text-brick-600 hover:text-brick-700 hover:bg-brick-50"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            {worker.total_advances > 0 && (
                              <Button
                                data-testid={`reset-advances-${worker.id}`}
                                onClick={() => {
                                  setSelectedWorker(worker.id);
                                  setShowResetAdvancesDialog(true);
                                }}
                                size="sm"
                                variant="ghost"
                                className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                                title="Reset Advances"
                              >
                                <RotateCcw className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              data-testid={`delete-worker-${worker.id}`}
                              onClick={() => {
                                setDeletingWorker(worker);
                                setShowDeleteWorkerDialog(true);
                              }}
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit Worker Dialog */}
      <Dialog open={showEditWorkerDialog} onOpenChange={setShowEditWorkerDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-manrope font-bold">Edit Worker</DialogTitle>
          </DialogHeader>
          {editingWorker && (
            <form onSubmit={handleUpdateWorker} className="space-y-4">
              <div>
                <Label className="font-manrope font-semibold uppercase tracking-wider text-xs">Worker Name</Label>
                <Input
                  value={editingWorker.name}
                  onChange={(e) => setEditingWorker({ ...editingWorker, name: e.target.value })}
                  className="mt-2"
                  required
                />
              </div>
              <div>
                <Label className="font-manrope font-semibold uppercase tracking-wider text-xs">Phone Number</Label>
                <Input
                  value={editingWorker.phone}
                  onChange={(e) => setEditingWorker({ ...editingWorker, phone: e.target.value })}
                  className="mt-2"
                  required
                />
              </div>
              <div className="flex gap-3">
                <Button type="submit" className="flex-1 bg-brick-600 hover:bg-brick-500">
                  Update Worker
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowEditWorkerDialog(false)} className="flex-1">
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Worker Dialog */}
      <AlertDialog open={showDeleteWorkerDialog} onOpenChange={setShowDeleteWorkerDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-manrope font-bold">Delete Worker?</AlertDialogTitle>
            <AlertDialogDescription className="font-inter">
              This will permanently delete worker <strong>{deletingWorker?.name}</strong> and all associated advance records. 
              Production entries must be deleted first if any exist. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteWorkerConfirm} className="bg-red-600 hover:bg-red-700">
              Delete Worker
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Advances Dialog */}
      <AlertDialog open={showResetAdvancesDialog} onOpenChange={setShowResetAdvancesDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-manrope font-bold">Reset Worker Advances?</AlertDialogTitle>
            <AlertDialogDescription className="font-inter">
              This will reset the total advances for this worker to ₹0.00. This is useful after settling all dues. 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleResetAdvances} className="bg-orange-600 hover:bg-orange-700">
              Reset Advances
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Pay Salary Dialog */}
      <Dialog open={showPaySalaryDialog} onOpenChange={setShowPaySalaryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-manrope font-bold flex items-center gap-2">
              <Banknote className="h-5 w-5 text-green-600" />
              Pay Salary - {payingSalary?.worker_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-concrete-50 rounded-md p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-concrete-600">Production Salary:</span>
                <span className="font-semibold">₹{payingSalary?.total_salary?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-concrete-600">Loading Earnings:</span>
                <span className="font-semibold text-green-600">+₹{payingSalary?.loading_earnings?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-concrete-600">Advances:</span>
                <span className="font-semibold text-red-600">-₹{payingSalary?.total_advances?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-concrete-600">Already Paid:</span>
                <span className="font-semibold text-blue-600">-₹{payingSalary?.salary_paid?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="border-t border-concrete-300 pt-2 flex justify-between">
                <span className="font-semibold text-concrete-700">Balance Due:</span>
                <span className="font-bold text-brick-600">₹{payingSalary?.final_salary?.toFixed(2) || '0.00'}</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="font-manrope font-semibold uppercase tracking-wider text-xs">Payment Date</Label>
                <Input
                  type="date"
                  value={payDate}
                  onChange={(e) => setPayDate(e.target.value)}
                  className="mt-2"
                />
              </div>
              <div>
                <Label className="font-manrope font-semibold uppercase tracking-wider text-xs">Amount (₹)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={payAmount}
                  onChange={(e) => setPayAmount(parseFloat(e.target.value) || 0)}
                  className="mt-2 tabular-nums"
                />
              </div>
            </div>
            <div>
              <Label className="font-manrope font-semibold uppercase tracking-wider text-xs">Notes (Optional)</Label>
              <Input
                type="text"
                placeholder="Payment notes..."
                value={payNotes}
                onChange={(e) => setPayNotes(e.target.value)}
                className="mt-2"
              />
            </div>
            <div className="flex gap-3">
              <Button 
                onClick={handlePaySalary} 
                className="flex-1 bg-green-600 hover:bg-green-700"
                disabled={payAmount <= 0}
              >
                <Banknote className="mr-2 h-4 w-4" />
                Pay ₹{payAmount.toFixed(2)}
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowPaySalaryDialog(false)} 
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Salary Details Dialog */}
      <Dialog open={showSalaryDetailsDialog} onOpenChange={setShowSalaryDetailsDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-manrope font-bold flex items-center gap-2 text-lg">
              <FileText className="h-5 w-5 text-brick-600" />
              Salary Details - {salaryDetails?.worker_name}
            </DialogTitle>
            <p className="text-sm text-concrete-500">
              Period: {salaryDetails?.date_range?.start} to {salaryDetails?.date_range?.end}
            </p>
          </DialogHeader>
          
          {salaryDetails && (
            <div className="space-y-6">
              {/* Summary Card */}
              <div className="bg-gradient-to-r from-brick-50 to-orange-50 border border-brick-200 rounded-lg p-4">
                <h3 className="font-semibold text-brick-800 mb-3">Summary</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                  <div className="bg-white rounded-md p-3 shadow-sm">
                    <p className="text-concrete-500">Production</p>
                    <p className="font-bold text-lg text-green-600">₹{salaryDetails.summary.total_production_salary.toFixed(2)}</p>
                  </div>
                  <div className="bg-white rounded-md p-3 shadow-sm">
                    <p className="text-concrete-500">Loading</p>
                    <p className="font-bold text-lg text-blue-600">₹{salaryDetails.summary.total_loading_salary.toFixed(2)}</p>
                  </div>
                  <div className="bg-white rounded-md p-3 shadow-sm">
                    <p className="text-concrete-500">Advances</p>
                    <p className="font-bold text-lg text-red-600">-₹{salaryDetails.summary.total_advances.toFixed(2)}</p>
                  </div>
                  <div className="bg-white rounded-md p-3 shadow-sm">
                    <p className="text-concrete-500">Paid</p>
                    <p className="font-bold text-lg text-orange-600">-₹{salaryDetails.summary.salary_paid.toFixed(2)}</p>
                  </div>
                  <div className="bg-white rounded-md p-3 shadow-sm border-2 border-brick-300">
                    <p className="text-concrete-500">Balance</p>
                    <p className="font-bold text-lg text-brick-600">₹{salaryDetails.summary.final_balance.toFixed(2)}</p>
                  </div>
                </div>
              </div>

              {/* Production Details */}
              <div>
                <h3 className="font-semibold text-concrete-800 mb-3 flex items-center gap-2">
                  <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs">PRODUCTION</span>
                  Date-wise Breakdown
                </h3>
                {salaryDetails.production_details.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border border-concrete-200 rounded-lg overflow-hidden">
                      <thead className="bg-green-50">
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold text-concrete-700">Date</th>
                          <th className="px-3 py-2 text-center font-semibold text-concrete-700">Fly Ash</th>
                          <th className="px-3 py-2 text-center font-semibold text-concrete-700">6" IL</th>
                          <th className="px-3 py-2 text-center font-semibold text-concrete-700">8" IL</th>
                          <th className="px-3 py-2 text-center font-semibold text-concrete-700">Other</th>
                          <th className="px-3 py-2 text-right font-semibold text-concrete-700">Day Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-concrete-100">
                        {salaryDetails.production_details.map((prod, idx) => (
                          <tr key={idx} className="hover:bg-concrete-50">
                            <td className="px-3 py-2 font-medium">{prod.date}</td>
                            <td className="px-3 py-2 text-center">
                              <span className="text-concrete-600">{prod.fly_ash}</span>
                              <span className="text-xs text-concrete-400 ml-1">× ₹{prod.fly_ash_rate}</span>
                              <span className="text-green-600 ml-1">= ₹{prod.fly_ash_salary.toFixed(2)}</span>
                            </td>
                            <td className="px-3 py-2 text-center">
                              <span className="text-concrete-600">{prod.interlock_6}</span>
                              <span className="text-xs text-concrete-400 ml-1">× ₹{prod.interlock_6_rate}</span>
                              <span className="text-green-600 ml-1">= ₹{prod.interlock_6_salary.toFixed(2)}</span>
                            </td>
                            <td className="px-3 py-2 text-center">
                              <span className="text-concrete-600">{prod.interlock_8}</span>
                              <span className="text-xs text-concrete-400 ml-1">× ₹{prod.interlock_8_rate}</span>
                              <span className="text-green-600 ml-1">= ₹{prod.interlock_8_salary.toFixed(2)}</span>
                            </td>
                            <td className="px-3 py-2 text-center text-green-600">₹{prod.other_salary.toFixed(2)}</td>
                            <td className="px-3 py-2 text-right font-semibold text-green-700">₹{prod.day_total.toFixed(2)}</td>
                          </tr>
                        ))}
                        <tr className="bg-green-100 font-semibold">
                          <td colSpan="5" className="px-3 py-2 text-right">Total Production:</td>
                          <td className="px-3 py-2 text-right text-green-700">₹{salaryDetails.summary.total_production_salary.toFixed(2)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-concrete-500 text-sm italic">No production entries in this period</p>
                )}
              </div>

              {/* Loading Details */}
              <div>
                <h3 className="font-semibold text-concrete-800 mb-3 flex items-center gap-2">
                  <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs">LOADING</span>
                  Date-wise Breakdown (Item-wise Details)
                </h3>
                {salaryDetails.loading_details.length > 0 ? (
                  <div className="space-y-4">
                    {salaryDetails.loading_details.map((load, idx) => (
                      <div key={idx} className="border border-blue-200 rounded-lg overflow-hidden">
                        <div className="bg-blue-50 px-4 py-2 flex justify-between items-center">
                          <div>
                            <span className="font-semibold text-blue-800">{load.date}</span>
                            <span className="ml-3 text-concrete-600">{load.customer_name}</span>
                          </div>
                          <span className="font-bold text-blue-700">₹{load.loading_charges.toFixed(2)}</span>
                        </div>
                        <table className="w-full text-sm">
                          <thead className="bg-concrete-50">
                            <tr>
                              <th className="px-3 py-1.5 text-left text-xs font-semibold text-concrete-600">Item</th>
                              <th className="px-3 py-1.5 text-center text-xs font-semibold text-concrete-600">Qty</th>
                              <th className="px-3 py-1.5 text-center text-xs font-semibold text-concrete-600">Rate/pc</th>
                              <th className="px-3 py-1.5 text-right text-xs font-semibold text-concrete-600">Amount</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-concrete-100">
                            {load.fly_ash > 0 && (
                              <tr>
                                <td className="px-3 py-1.5">Fly Ash</td>
                                <td className="px-3 py-1.5 text-center">{load.fly_ash}</td>
                                <td className="px-3 py-1.5 text-center text-concrete-500">₹{load.fly_ash_rate || 0}</td>
                                <td className="px-3 py-1.5 text-right">₹{(load.fly_ash_loading || 0).toFixed(2)}</td>
                              </tr>
                            )}
                            {load.interlock_6 > 0 && (
                              <tr>
                                <td className="px-3 py-1.5">6" Interlock</td>
                                <td className="px-3 py-1.5 text-center">{load.interlock_6}</td>
                                <td className="px-3 py-1.5 text-center text-concrete-500">₹{load.interlock_6_rate || 0}</td>
                                <td className="px-3 py-1.5 text-right">₹{(load.interlock_6_loading || 0).toFixed(2)}</td>
                              </tr>
                            )}
                            {load.interlock_8 > 0 && (
                              <tr>
                                <td className="px-3 py-1.5">8" Interlock</td>
                                <td className="px-3 py-1.5 text-center">{load.interlock_8}</td>
                                <td className="px-3 py-1.5 text-center text-concrete-500">₹{load.interlock_8_rate || 0}</td>
                                <td className="px-3 py-1.5 text-right">₹{(load.interlock_8_loading || 0).toFixed(2)}</td>
                              </tr>
                            )}
                            {load.custom_materials && load.custom_materials.filter(cm => cm.qty > 0).map((cm, cIdx) => (
                              <tr key={cIdx}>
                                <td className="px-3 py-1.5">{cm.name}</td>
                                <td className="px-3 py-1.5 text-center">{cm.qty}</td>
                                <td className="px-3 py-1.5 text-center text-concrete-500">₹{cm.rate || 0}</td>
                                <td className="px-3 py-1.5 text-right">₹{((cm.qty || 0) * (cm.rate || 0)).toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}
                    <div className="bg-blue-100 rounded-lg px-4 py-3 flex justify-between items-center font-semibold">
                      <span className="text-blue-800">Total Loading Earnings:</span>
                      <span className="text-blue-700 text-lg">₹{salaryDetails.summary.total_loading_salary.toFixed(2)}</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-concrete-500 text-sm italic">No loading charges in this period</p>
                )}
              </div>

              {/* Advances Details */}
              {salaryDetails.advances_details.length > 0 && (
                <div>
                  <h3 className="font-semibold text-concrete-800 mb-3 flex items-center gap-2">
                    <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs">ADVANCES</span>
                    Date-wise Breakdown
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border border-concrete-200 rounded-lg overflow-hidden">
                      <thead className="bg-red-50">
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold text-concrete-700">Date</th>
                          <th className="px-3 py-2 text-left font-semibold text-concrete-700">Notes</th>
                          <th className="px-3 py-2 text-right font-semibold text-concrete-700">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-concrete-100">
                        {salaryDetails.advances_details.map((adv, idx) => (
                          <tr key={idx} className="hover:bg-concrete-50">
                            <td className="px-3 py-2 font-medium">{adv.date}</td>
                            <td className="px-3 py-2 text-concrete-600">{adv.notes || '-'}</td>
                            <td className="px-3 py-2 text-right font-semibold text-red-600">₹{adv.amount.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Payments Details */}
              {salaryDetails.payments_details && salaryDetails.payments_details.length > 0 && (
                <div>
                  <h3 className="font-semibold text-concrete-800 mb-3 flex items-center gap-2">
                    <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs">PAYMENTS</span>
                    Date-wise Breakdown
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border border-concrete-200 rounded-lg overflow-hidden">
                      <thead className="bg-green-50">
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold text-concrete-700">Date</th>
                          <th className="px-3 py-2 text-left font-semibold text-concrete-700">Notes</th>
                          <th className="px-3 py-2 text-right font-semibold text-concrete-700">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-concrete-100">
                        {salaryDetails.payments_details.map((pay, idx) => (
                          <tr key={idx} className="hover:bg-concrete-50">
                            <td className="px-3 py-2 font-medium">{pay.date}</td>
                            <td className="px-3 py-2 text-concrete-600">{pay.notes || '-'}</td>
                            <td className="px-3 py-2 text-right font-semibold text-green-600">₹{pay.amount.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}